import { Injectable } from '@angular/core';
import { PlaylistTrack, Track } from '@app/core/models/Track';
import { ArchivedTrack } from '@app/library/models/trash.model';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import {
  appendWithUniq,
  insertItemWithUniq,
  removeItemWithId,
  removeItemWithIdOrPosition,
} from '@radioking/xs-state-operators';

import {
  SelectArchivedTrack,
  SelectMultipleArchivedTracks,
  SelectMultiplePlayTracks,
  SelectMultipleTracks,
  SelectPlayTrack,
  SelectTrack,
  ToggleSelectArchivedTrack,
  ToggleSelectPlayTrack,
  ToggleSelectTrack,
  UnselectAll,
  UnselectArchivedTrack,
  UnselectPlayTrack,
  UnselectTrack,
} from './selected-tracks.actions';

export class SelectedTracksStateModel {
  tracks: Track[];
  playlistTracks: PlaylistTrack[];
  archivedTracks: ArchivedTrack[];
  lastClickedTrack: number;
}

@State<SelectedTracksStateModel>({
  name: 'selectedTracks',
  defaults: {
    tracks: [],
    playlistTracks: [],
    archivedTracks: [],
    lastClickedTrack: 0,
  },
})
@Injectable()
export class SelectedTracksState {
  @Selector()
  static isTrackSelected(state: SelectedTracksStateModel): (a: Track) => boolean {
    return (val: Track) => state.tracks.findIndex(track => track.id === val.id) >= 0;
  }

  @Selector()
  static isPlayTrackSelected(
    state: SelectedTracksStateModel,
  ): (a: PlaylistTrack) => boolean {
    return (val: PlaylistTrack) =>
      state.playlistTracks.findIndex(
        track => track.id === val.id && track.position === val.position,
      ) >= 0;
  }

  @Selector()
  static isArchivedTrackSelected(
    state: SelectedTracksStateModel,
  ): (a: ArchivedTrack) => boolean {
    return (val: ArchivedTrack) =>
      state.archivedTracks.findIndex(archivedTrack => archivedTrack.id === val.id) >= 0;
  }

  @Selector()
  static selectedTracks(state: SelectedTracksStateModel): number[] {
    return state.tracks.map(track => track.id);
  }

  @Selector()
  static selectedTracksObjects(state: SelectedTracksStateModel): Track[] {
    return state.tracks;
  }

  @Selector()
  static selectedPlayTracks(state: SelectedTracksStateModel): PlaylistTrack[] {
    return state.playlistTracks;
  }

  @Selector()
  static selectedArchivedTracks(state: SelectedTracksStateModel): number[] {
    return state.archivedTracks.map(archivedTrack => archivedTrack.id);
  }

  @Selector()
  static numberTracksSelected(state: SelectedTracksStateModel): number {
    return state.tracks.length;
  }

  @Selector()
  static numberPlayTrackSelected(state: SelectedTracksStateModel): number {
    return state.playlistTracks.length;
  }

  @Selector()
  static numberArchivedTracksSelected(state: SelectedTracksStateModel): number {
    return state.archivedTracks.length;
  }

  @Selector()
  static lastClicked(state: SelectedTracksStateModel): number {
    return state.lastClickedTrack;
  }

  @Action(SelectTrack)
  selectTrack(
    ctx: StateContext<SelectedTracksStateModel>,
    { track, clicked }: SelectTrack,
  ) {
    ctx.setState(
      patch({
        tracks: insertItemWithUniq(track),
      }),
    );
    if (clicked) {
      ctx.patchState({ lastClickedTrack: track.id });
    }
  }

  @Action(UnselectTrack)
  unselectTrack(ctx: StateContext<SelectedTracksStateModel>, { track }: UnselectTrack) {
    ctx.setState(
      patch({
        tracks: removeItemWithId(track),
      }),
    );
  }

  @Action(SelectMultipleTracks)
  selectMultipleTrack(
    ctx: StateContext<SelectedTracksStateModel>,
    { tracks }: SelectMultipleTracks,
  ) {
    ctx.setState(
      patch({
        tracks: appendWithUniq(tracks),
      }),
    );
  }

  @Action(ToggleSelectTrack)
  toggleTrack(ctx: StateContext<SelectedTracksStateModel>, { track }: ToggleSelectTrack) {
    let action: SelectTrack | UnselectTrack;
    if (SelectedTracksState.isTrackSelected(ctx.getState())(track)) {
      action = new UnselectTrack(track);
    } else {
      action = new SelectTrack(track, true);
    }

    return ctx.dispatch(action);
  }

  @Action(SelectPlayTrack)
  selectPlayTrack(
    ctx: StateContext<SelectedTracksStateModel>,
    { track, clicked }: SelectPlayTrack,
  ) {
    ctx.setState(
      patch({
        playlistTracks: insertItemWithUniq(track),
      }),
    );
    if (clicked) {
      ctx.patchState({ lastClickedTrack: track.position ? track.position : track.id });
    }
  }

  @Action(UnselectPlayTrack)
  unselectPlayTrack(
    ctx: StateContext<SelectedTracksStateModel>,
    { track }: UnselectPlayTrack,
  ) {
    ctx.setState(
      patch({
        playlistTracks: removeItemWithIdOrPosition(track),
      }),
    );
  }

  @Action(SelectMultiplePlayTracks)
  selectMultiplePlayTrack(
    ctx: StateContext<SelectedTracksStateModel>,
    { tracks }: SelectMultiplePlayTracks,
  ) {
    ctx.setState(
      patch({
        playlistTracks: appendWithUniq(tracks),
      }),
    );
  }

  @Action(ToggleSelectPlayTrack)
  togglePlayTrack(
    ctx: StateContext<SelectedTracksStateModel>,
    { track, clicked }: ToggleSelectPlayTrack,
  ) {
    let action: SelectPlayTrack | UnselectPlayTrack;
    if (SelectedTracksState.isPlayTrackSelected(ctx.getState())(track)) {
      action = new UnselectPlayTrack(track);
    } else {
      action = new SelectPlayTrack(track, clicked);
    }

    return ctx.dispatch(action);
  }

  @Action(SelectArchivedTrack)
  selectArchivedTrack(
    ctx: StateContext<SelectedTracksStateModel>,
    { archivedTrack, clicked }: SelectArchivedTrack,
  ) {
    ctx.setState(
      patch({
        archivedTracks: insertItemWithUniq(archivedTrack),
      }),
    );
    if (clicked) {
      ctx.patchState({ lastClickedTrack: archivedTrack.id });
    }
  }

  @Action(UnselectArchivedTrack)
  unselectArchivedTrack(
    ctx: StateContext<SelectedTracksStateModel>,
    { archivedTrack }: UnselectArchivedTrack,
  ) {
    ctx.setState(
      patch({
        archivedTracks: removeItemWithId(archivedTrack),
      }),
    );
  }

  @Action(SelectMultipleArchivedTracks)
  selectMultipleArchivedTrack(
    ctx: StateContext<SelectedTracksStateModel>,
    { archivedTracks }: SelectMultipleArchivedTracks,
  ) {
    ctx.setState(
      patch({
        archivedTracks: appendWithUniq(archivedTracks),
      }),
    );
  }

  @Action(ToggleSelectArchivedTrack)
  toggleArchivedTrack(
    ctx: StateContext<SelectedTracksStateModel>,
    { archivedTrack }: ToggleSelectArchivedTrack,
  ) {
    let action: SelectArchivedTrack | UnselectArchivedTrack;
    if (SelectedTracksState.isArchivedTrackSelected(ctx.getState())(archivedTrack)) {
      action = new UnselectArchivedTrack(archivedTrack);
    } else {
      action = new SelectArchivedTrack(archivedTrack, true);
    }

    return ctx.dispatch(action);
  }

  @Action(UnselectAll)
  unselectMultipleTrack(ctx: StateContext<SelectedTracksStateModel>) {
    const oldState = ctx.getState();
    if (
      oldState.tracks.length === 0 &&
      oldState.playlistTracks.length === 0 &&
      oldState.archivedTracks.length === 0
    ) {
      return;
    }
    ctx.setState(
      patch<SelectedTracksStateModel>({
        tracks: [],
        playlistTracks: [],
        archivedTracks: [],
      }),
    );
  }
}
